import React, { useState, useEffect } from 'react';
import { getAllComments, updateComment, deleteComment } from '../../services/commentService';
import { getAllBlogs } from '../../services/blogService';
import StatusBadge from '../../components/admin/StatusBadge';
import CardSkeleton from '../../components/admin/CardSkeleton';

const AdminComments = () => {
  const [comments, setComments] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [filteredComments, setFilteredComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [blogFilter, setBlogFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedComments, setSelectedComments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingComment, setEditingComment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({ name: '', email: '', comment: '' });
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterComments();
  }, [comments, statusFilter, blogFilter, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [commentsData, blogsData] = await Promise.all([
        getAllComments(),
        getAllBlogs()
      ]);
      console.log('Fetched comments data:', commentsData);
      console.log('First comment structure:', commentsData[0]);
      setComments(commentsData);
      setBlogs(blogsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterComments = () => {
    let filtered = comments;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(comment =>
        comment.comment.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comment.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(comment => comment.status === statusFilter);
    }

    // Blog filter
    if (blogFilter !== 'all') {
      filtered = filtered.filter(comment => comment.blogId === blogFilter);
    }

    setFilteredComments(filtered);
    setCurrentPage(1);
    setSelectedComments([]);
  };

  const handleStatusChange = async (commentId, newStatus) => {
    try {
      await updateComment(commentId, { status: newStatus });
      fetchData(); // Refresh data
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      try {
        await deleteComment(commentId);
        fetchData();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedComments.length === 0) {
      alert('Please select comments to perform bulk action.');
      return;
    }

    const confirmMessage = action === 'delete'
      ? `Are you sure you want to delete ${selectedComments.length} comment(s)?`
      : `Are you sure you want to ${action} ${selectedComments.length} comment(s)?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const promises = selectedComments.map(commentId => {
        if (action === 'delete') {
          return deleteComment(commentId);
        } else {
          return updateComment(commentId, { status: action });
        }
      });

      await Promise.all(promises);
      fetchData();
      setSelectedComments([]);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedComments(paginatedComments.map(comment => comment.id));
    } else {
      setSelectedComments([]);
    }
  };

  const handleSelectComment = (commentId, checked) => {
    if (checked) {
      setSelectedComments(prev => [...prev, commentId]);
    } else {
      setSelectedComments(prev => prev.filter(id => id !== commentId));
    }
  };

  const handleEdit = (comment) => {
    setEditingComment(comment);
    setEditFormData({
      name: comment.name || '',
      email: comment.email || '',
      comment: comment.comment || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateComment(editingComment.id, {
        name: editFormData.name,
        email: editFormData.email || null,
        comment: editFormData.comment
      });
      setShowEditModal(false);
      setEditingComment(null);
      fetchData(); // Refresh data
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingComment(null);
    setEditFormData({ name: '', email: '', comment: '' });
  };

  const getBlogTitle = (blogId) => {
    const blog = blogs.find(b => b.id === blogId);
    return blog ? blog.title : 'Unknown Blog';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredComments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedComments = filteredComments.slice(startIndex, startIndex + itemsPerPage);
  console.log('Paginated comments:', paginatedComments);

  if (loading) {
    return <CardSkeleton height="600px" />;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Error Loading Comments</h4>
        <p>{error}</p>
        <button className="btn btn-outline-danger" onClick={fetchData}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Comment Moderation</h2>
        {selectedComments.length > 0 && (
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-success btn-sm"
              onClick={() => handleBulkAction('approved')}
            >
              Approve Selected ({selectedComments.length})
            </button>
            <button
              className="btn btn-outline-warning btn-sm"
              onClick={() => handleBulkAction('rejected')}
            >
              Reject Selected ({selectedComments.length})
            </button>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleBulkAction('delete')}
            >
              Delete Selected ({selectedComments.length})
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search comments or authors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={blogFilter}
                onChange={(e) => setBlogFilter(e.target.value)}
              >
                <option value="all">All Blogs</option>
                {blogs.map(blog => (
                  <option key={blog.id} value={blog.id}>
                    {blog.title.length > 30 ? blog.title.substring(0, 30) + '...' : blog.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setBlogFilter('all');
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="mb-3 text-muted">
        Showing {paginatedComments.length} of {filteredComments.length} comments
        {selectedComments.length > 0 && ` (${selectedComments.length} selected)`}
      </div>

      {/* Comments Table */}
      <div className="admin-table">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedComments.length === paginatedComments.length && paginatedComments.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>Blog Post</th>
              <th>Author</th>
              <th>Comment</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedComments.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-5 text-muted">
                  <i className="bi bi-chat-x fs-1 mb-3 d-block"></i>
                  No comments found matching your criteria.
                </td>
              </tr>
            ) : (
              paginatedComments.map(comment => (
                <tr key={comment.id}>
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedComments.includes(comment.id)}
                      onChange={(e) => handleSelectComment(comment.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <div className="fw-medium text-truncate" style={{ maxWidth: '200px' }}>
                      {getBlogTitle(comment.blogId)}
                    </div>
                  </td>
                  <td>
                    <div className="fw-medium">{comment.name}</div>
                    {comment.email && (
                      <small className="text-muted">{comment.email}</small>
                    )}
                  </td>
                  <td>
                    <div className="text-truncate" style={{ maxWidth: '300px' }}>
                      {comment.comment}
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={comment.status} type="comment" />
                  </td>
                  <td>
                    <small className="text-muted">
                      {formatDate(comment.createdAt)}
                    </small>
                  </td>
                  <td>
                    <div className="btn-group" role="group">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(comment)}
                        title="Edit"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      {comment.status !== 'approved' && (
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => handleStatusChange(comment.id, 'approved')}
                          title="Approve"
                        >
                          <i className="bi bi-check-circle"></i>
                        </button>
                      )}
                      {comment.status !== 'rejected' && (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleStatusChange(comment.id, 'rejected')}
                          title="Reject"
                        >
                          <i className="bi bi-x-circle"></i>
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(comment.id)}
                        title="Delete"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
            </li>
            {[...Array(totalPages)].map((_, index) => (
              <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}

      {/* Edit Comment Modal */}
      {showEditModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Comment</h5>
                <button type="button" className="btn-close" onClick={handleEditCancel}></button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="editName" className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="editName"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="editEmail" className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      id="editEmail"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="editComment" className="form-label">Comment</label>
                    <textarea
                      className="form-control"
                      id="editComment"
                      rows="4"
                      value={editFormData.comment}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, comment: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleEditCancel}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminComments;